/**
 * Role-aware account dropdown for the public navbar (desktop).
 */

import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logoutUser } from '../../store/features/auth/authThunks';
import { IUser } from '../../types/auth.types';
import { getUserDisplayName, getUserInitials } from '../../utils/userHelpers';
import { buildUserNavMenuConfig, PERSONA_ACCENT_STYLES } from '../../config/userNavMenu';
import { useNavPersona } from '../../contexts/NavPersonaContext';
import { useNavbarBadges } from '../../hooks/useNavbarBadges';
import { UserNavMenuPanel } from './UserNavMenuPanel';
import { userNavPanelVariants, userNavTransition } from './userNavMotion';

interface UserMenuProps {
  user: IUser;
}

export function UserMenu({ user }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const reduceMotion = useReducedMotion();
  const { menuPersona } = useNavPersona();
  const { count: badgeCount } = useNavbarBadges(menuPersona, true);
  const menu = buildUserNavMenuConfig(user, location.pathname);
  const accent = PERSONA_ACCENT_STYLES[menu.accent];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setIsOpen(false);
    await dispatch(logoutUser());
  };

  const close = () => setIsOpen(false);

  return (
    <div className="navbar__account-menu" ref={dropdownRef}>
      <button
        type="button"
        data-testid="navbar-account-menu-trigger-desktop"
        data-persona={menu.accent}
        onClick={() => setIsOpen((v) => !v)}
        className={`navbar__account-trigger${isOpen ? ' is-open' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span
          className={`navbar__account-avatar bg-gradient-to-br ${accent.avatarGradient}`}
        >
          {getUserInitials(user)}
          {badgeCount > 0 ? (
            <span className="navbar__account-badge">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          ) : null}
        </span>
        <span className="navbar__account-meta">
          <span className="navbar__account-name">{getUserDisplayName(user)}</span>
          <span className="navbar__account-role">{menu.personaLabel}</span>
        </span>
        <span className="navbar__account-chevron" aria-hidden>
          <ChevronDownIcon />
        </span>
      </button>

      <AnimatePresence>
        {isOpen ? (
          <>
            <motion.div
              className="fixed inset-0 z-10"
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={userNavTransition(reduceMotion)}
              onClick={close}
            />

            <motion.div
              className={`absolute right-0 z-20 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border ${accent.dropdownShell} !bg-white/95 backdrop-blur-xl`}
              variants={userNavPanelVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={userNavTransition(reduceMotion)}
            >
              <UserNavMenuPanel
                user={user}
                variant="dropdown"
                onNavigate={close}
                onLogout={handleLogout}
              />
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
